<template>
  <v2-modal
    :show="show"
    size="full"
    body-id="v2-buying-summary-modal-body"
    @close="$emit('close')"
  >
    <template #header>
      <div class="v2-buying-summary-modal__header">
        <div class="v2-buying-summary-modal__title">
          Buying Summary
        </div>
        <div class="v2-buying-summary-modal__header-right">
          <div
            v-if="isMultipleCart"
            class="v2-buying-summary-modal__badges"
          >
            <v2-badge variant="default">
              Total units: {{ formatNumber(totalOrderValue.totalCartsUnits) || 0 }}
            </v2-badge>
            <v2-badge variant="default">
              Total cost: {{ formatCurrency(Number(totalOrderValue.totalCartsValue), totalOrderValue.currency) }}
            </v2-badge>
            <v2-badge variant="default">
              Total RRP: {{ formatCurrency(Number(totalOrderValue.totalCartsRRP), totalOrderValue.currency) }}
            </v2-badge>
          </div>
          <div class="v2-buying-summary-modal__filter-wrapper">
            <v2-button
              variant="outline"
              size="sm"
              @click="toggleFilter"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>Filter</span>
            </v2-button>
            <div
              v-if="showFilter"
              class="v2-buying-summary-modal__filter-dropdown"
            >
              <div class="v2-buying-summary-modal__filter-options">
                <div
                  v-for="groupName in filterGroups"
                  :key="groupName"
                >
                  <div class="v2-buying-summary-modal__filter-heading">
                    {{ groupName }}
                  </div>
                  <div class="v2-buying-summary-modal__filter-checkboxes">
                    <label
                      v-for="group in Object.keys(productGroups[groupName])"
                      :key="group"
                      class="v2-buying-summary-modal__filter-checkbox"
                    >
                      <input
                        v-model="filteredProducts[groupName]"
                        type="checkbox"
                        :value="group"
                        @change="updateFilter(groupName)"
                      >
                      <span class="v2-buying-summary-modal__checkbox-label">
                        <span>{{ group }}</span>
                        <span class="v2-buying-summary-modal__group-count">{{ productGroups[groupName][group] }}</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div
      v-for="(cart, index) in cartList"
      :key="'buying_summary' + cart._id"
    >
      <v2-buying-summary-store
        :cart="cart"
        :cart-value="cartValuesList[index]"
        :set-table-items="initBuyingSummary"
        :table-fields="tablesFields[index]"
        :show-store-header="isMultipleCart"
      />
    </div>
  </v2-modal>
</template>

<script>
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import { formatNumber } from '@/@core/utils/utils'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import { filterOptionCount } from '@/views/apps/checkout-v2/buying-summary-utils'
import V2BuyingSummaryStore from './V2BuyingSummaryStore.vue'

const { formatCurrency } = utils
const { CURRENCY_CONVERSIONS } = c

export default {
  name: 'V2BuyingSummaryModal',
  components: {
    V2Modal,
    V2Button,
    V2Badge,
    V2BuyingSummaryStore,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    cartList: {
      type: Array,
      required: true,
    },
    cartValuesList: {
      type: Array,
      required: true,
    },
    isMultipleCart: {
      type: Boolean,
      default: true,
    },
    totalOrderValue: {
      type: Object,
      default: () => ({
        totalCartsValue: 0,
        totalCartsUnits: 0,
        totalCartsRRP: 0,
        currency: CURRENCY_CONVERSIONS.USD.SYMBOL,
      }),
    },
  },
  data() {
    return {
      formatCurrency,
      formatNumber,
      showFilter: false,
      filterGroups: ['Department', 'Category', 'SubCategory', 'Gender'],
      productGroups: {
        Department: [],
        Category: [],
        SubCategory: [],
        Gender: [],
      },
      filteredProducts: {
        Department: [],
        Category: [],
        SubCategory: [],
        Gender: [],
      },
      tablesItems: [],
      tablesFields: [],
    }
  },
  methods: {
    toggleFilter() {
      this.showFilter = !this.showFilter
      this.trackFilterButton()
    },
    initBuyingSummary(tableItems) {
      if (tableItems.length) {
        this.tablesItems.push(tableItems)
        this.tablesFields.push(this.filterProduct('', tableItems))
      }
    },
    trackFilterButton() {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_BUYING_SUMMARY_FILTER_BUTTON, {
        carts: this.cartList,
      })
    },
    filterProduct(group = '', tableItems = []) {
      // Filtering Product with user Input
      const filteringResult = {
        tableItems,
        Department: null,
        SubCategory: null,
        Category: null,
        Gender: null,
      }
      const keys = Object.keys(this.filteredProducts)
      const items = ['tableItems', 'Department', 'Category', 'SubCategory']
      keys.forEach((key, index) => {
        filteringResult[key] = filteringResult[items[index]].filter(item => this.filteredProducts[key].every(() => this.filteredProducts[key].includes(String(item[key]))))
        if (filteringResult[key].length === 0) filteringResult[key] = filteringResult[items[index]]
      })
      if (filteringResult.Gender.length === 0) filteringResult.Gender = tableItems
      // Setting Updated Filters

      const resultFilter = filterOptionCount(filteringResult.Gender)
      keys.forEach(key => {
        const isGroupNotDefined = !group
        const isKeyDifferentFromGroup = key !== group
        const hasNoFilteredProductsForKey = this.filteredProducts[key].length === 0
        const shouldUpdateProductGroup = isGroupNotDefined || (isKeyDifferentFromGroup && hasNoFilteredProductsForKey)
        if (shouldUpdateProductGroup) {
          this.productGroups[key] = resultFilter[key]
        }
      })
      return filteringResult.Gender
    },
    updateFilter(group) {
      this.tablesItems.forEach((tableItems, index) => {
        this.$set(this.tablesFields, index, this.filterProduct(group, tableItems))
      })
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SELECTS_BUYING_SUMMARY_FILTER_OPTION, {
        carts: this.cartList,
        filterOption: group,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-buying-summary-modal {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-foreground);
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    flex-wrap: wrap;
  }

  &__filter-wrapper {
    position: relative;
  }

  &__filter-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 10;
    margin-top: var(--v2-space-1);
    background: var(--v2-card);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
  }

  &__filter-options {
    width: 350px;
    max-height: 500px;
    overflow: auto;
    padding: var(--v2-space-4);
  }

  &__filter-heading {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
    margin-top: var(--v2-space-3);

    &:first-child {
      margin-top: 0;
    }
  }

  &__filter-checkboxes {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
    margin-bottom: var(--v2-space-3);
  }

  &__filter-checkbox {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--v2-foreground);

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      accent-color: var(--v2-primary);
      cursor: pointer;
      flex-shrink: 0;
    }
  }

  &__checkbox-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    min-width: 0;
  }

  &__group-count {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    background: var(--v2-muted);
    padding: var(--v2-space-0-5) var(--v2-space-2);
    border-radius: var(--v2-radius-full);
    margin-left: var(--v2-space-2);
  }

}
</style>

<style lang="scss">
/*
 * Buying Summary: scroll the table list inside the modal body instead of
 * the whole modal. Without this, the .v2-modal itself grows past the
 * viewport on long carts and the .v2-modal-overlay scrolls, taking the
 * sticky header out of view. Targeted via the modal's body id so this
 * doesn't change behavior for other V2Modal consumers.
 *
 * Approach:
 *   1. Constrain .v2-modal (the dialog box) to viewport height minus the
 *      overlay's vertical padding (--v2-space-8 each side ≈ 4rem total).
 *   2. Let the body grow within that constraint and scroll internally.
 *      min-height: 0 is required for a flex child to shrink below its
 *      intrinsic content size and start scrolling.
 */
.v2-modal-overlay:has(#v2-buying-summary-modal-body) > .v2-modal {
  max-height: calc(100vh - var(--v2-space-8) * 2);
}

#v2-buying-summary-modal-body {
  overflow-y: auto;
  overflow-x: auto;
  min-height: 0;
  // The modal body is the scrolling ancestor for the sticky table header
  // inside V2BuyingSummaryStore — padding-top is 0 so the sticky thead
  // pins flush to the top of the scroll viewport instead of leaving a
  // padded gap where rows would briefly appear above the header.
  padding-top: 0;
}
</style>
